import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../ui/select';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Clock, Globe, Calendar, Plus, XCircle, Loader2, Save, Copy, RefreshCw, CalendarDays } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HOURS = Array.from({ length: 24 }, (_, i) => i);

// Team Calendar Link Component for Admin
function TeamCalendarLink() {
  const [adminToken, setAdminToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetchToken();
  }, []);

  const fetchToken = async () => {
    try {
      const res = await axios.get(`${API}/ical/admin-token`);
      setAdminToken(res.data.admin_ical_token || '');
    } catch (err) {
      console.error('Error fetching admin token:', err);
    } finally {
      setLoading(false);
    }
  };

  const regenerateToken = async () => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/ical/admin-token/regenerate`);
      setAdminToken(res.data.admin_ical_token);
      toast.success('Nouveau lien généré');
    } catch (err) {
      toast.error('Erreur lors de la régénération');
    } finally {
      setRegenerating(false);
    }
  };

  const getIcalUrl = (useWebcal = false) => {
    if (!adminToken) return '';
    const baseUrl = 'https://staff.vitalis-sports.ch';
    let url = `${baseUrl}/api/ical/all/${adminToken}.ics`;
    if (useWebcal) {
      url = url.replace(/^https?:\/\//, 'webcal://');
    }
    return url;
  };

  const copyToClipboard = async (useWebcal = false) => {
    const url = getIcalUrl(useWebcal);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Lien copié !');
    } catch (err) {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Lien copié !');
    }
  };

  if (loading) {
    return (
      <div className="border-t border-border pt-6">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm text-muted-foreground">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-border pt-6">
      <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
        <CalendarDays className="h-5 w-5" />
        Calendrier Équipe (iCal)
      </h3>
      <p className="text-sm text-muted-foreground mb-4">
        Ce lien permet de synchroniser le planning de <strong>toute l'équipe</strong> dans une application calendrier.
      </p>

      <div className="space-y-4">
        {/* HTTPS Link */}
        <div className="space-y-2">
          <Label>Lien HTTPS (Google Calendar, Outlook)</Label>
          <div className="flex gap-2">
            <Input
              value={getIcalUrl(false)}
              readOnly
              className="text-xs font-mono"
            />
            <Button variant="outline" size="icon" onClick={() => copyToClipboard(false)} title="Copier">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Webcal Link */}
        <div className="space-y-2">
          <Label>Lien webcal:// (iPhone, iPad, Mac)</Label>
          <div className="flex gap-2">
            <Input
              value={getIcalUrl(true)}
              readOnly
              className="text-xs font-mono"
            />
            <Button variant="outline" size="icon" onClick={() => copyToClipboard(true)} title="Copier">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={regenerateToken}
            disabled={regenerating}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${regenerating ? 'animate-spin' : ''}`} />
            Régénérer le lien
          </Button>
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
          <p className="text-xs text-amber-700 dark:text-amber-400">
            ⚠️ Ce lien donne accès au planning de <strong>tous les collaborateurs</strong>. Ne le partagez qu'avec les personnes autorisées.
          </p>
        </div>
      </div>
    </div>
  );
}

// Holidays List Component
function HolidaysList({ language }) {
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newHoliday, setNewHoliday] = useState({ date: '', name_fr: '', name_de: '', name_en: '' });

  useEffect(() => {
    fetchHolidays();
  }, [year]);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/holidays/config`, { params: { year } });
      setHolidays(res.data.holidays || []);
    } catch (err) {
      console.error('Error fetching holidays:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (date) => {
    const updated = holidays.map(h => 
      h.date === date ? { ...h, non_worked: !h.non_worked } : h
    );
    setHolidays(updated);
    
    setSaving(true);
    try {
      const nonWorkedDates = updated.filter(h => h.non_worked).map(h => h.date);
      await axios.put(`${API}/holidays/config`, { year, non_worked: nonWorkedDates });
      toast.success('Configuration mise à jour');
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
      fetchHolidays();
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.date || !newHoliday.name_fr) {
      toast.error('Date et nom requis');
      return;
    }
    
    try {
      await axios.post(`${API}/holidays/custom`, newHoliday);
      toast.success('Jour férié ajouté');
      setShowAddDialog(false);
      setNewHoliday({ date: '', name_fr: '', name_de: '', name_en: '' });
      fetchHolidays();
    } catch (err) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteHoliday = async (date) => {
    if (!window.confirm('Supprimer ce jour férié personnalisé ?')) return;
    
    try {
      await axios.delete(`${API}/holidays/custom/${date}`);
      toast.success('Jour férié supprimé');
      fetchHolidays();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'de' ? 'de-CH' : language === 'en' ? 'en-GB' : 'fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  return (
    <div className="border-t border-border pt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <Calendar className="h-5 w-5" />
          Jours fériés {year}
        </h3>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setYear(y => y - 1)} disabled={loading}>
            {year - 1}
          </Button>
          <span className="font-medium px-2">{year}</span>
          <Button variant="outline" size="sm" onClick={() => setYear(y => y + 1)} disabled={loading}>
            {year + 1}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">Date</th>
                <th className="px-4 py-2 text-left text-sm font-medium">Jour férié</th>
                <th className="px-4 py-2 text-center text-sm font-medium">Non travaillé</th>
                <th className="px-4 py-2 text-center text-sm font-medium w-16"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {holidays.map((holiday) => (
                <tr key={holiday.date} className="hover:bg-muted/30">
                  <td className="px-4 py-3 text-sm">{formatDate(holiday.date)}</td>
                  <td className="px-4 py-3 text-sm font-medium">
                    {holiday.names[language] || holiday.names.fr}
                    {holiday.custom && (
                      <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded">
                        Personnalisé
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      type="button"
                      onClick={() => handleToggle(holiday.date)}
                      disabled={saving}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                        holiday.non_worked ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          holiday.non_worked ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {holiday.custom && (
                      <button
                        onClick={() => handleDeleteHoliday(holiday.date)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Supprimer"
                      >
                        <XCircle className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Les jours cochés "Non travaillé" seront automatiquement exclus des calculs d'heures.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setNewHoliday({ date: `${year}-01-01`, name_fr: '', name_de: '', name_en: '' });
            setShowAddDialog(true);
          }}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Ajouter un jour férié
        </Button>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un jour férié</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={newHoliday.date}
                onChange={(e) => setNewHoliday({ ...newHoliday, date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Nom (français) *</Label>
              <Input
                placeholder="Ex: Jour de congé entreprise"
                value={newHoliday.name_fr}
                onChange={(e) => setNewHoliday({ ...newHoliday, name_fr: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Nom (allemand)</Label>
              <Input
                placeholder="Ex: Betriebsurlaub"
                value={newHoliday.name_de}
                onChange={(e) => setNewHoliday({ ...newHoliday, name_de: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Nom (anglais)</Label>
              <Input
                placeholder="Ex: Company holiday"
                value={newHoliday.name_en}
                onChange={(e) => setNewHoliday({ ...newHoliday, name_en: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Annuler</Button>
            <Button onClick={handleAddHoliday}>Ajouter</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminScheduleConfig({ config, setConfig, onSave, saving, language }) {
  const { t } = useLanguage();

  return (
    <div className="border border-border rounded-lg p-6 bg-card space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{t('workHoursConfiguration')}</h2>
        <p className="text-sm text-muted-foreground">{t('workHoursDescription')}</p>
      </div>

      {/* Portal Language */}
      <div className="border-b border-border pb-6">
        <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
          <Globe className="h-5 w-5" />
          {t('portalLanguage')}
        </h3>
        <div className="max-w-xs space-y-2">
          <Label>{t('defaultLanguage')}</Label>
          <Select
            value={config.default_portal_language || 'de'}
            onValueChange={(v) => setConfig({ ...config, default_portal_language: v })}
          >
            <SelectTrigger data-testid="default-language-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
              <SelectItem value="fr">🇫🇷 Français</SelectItem>
              <SelectItem value="en">🇬🇧 English</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Work Hours */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
        <div className="space-y-2">
          <Label>{t('dayStartHour')}</Label>
          <Select
            value={config.work_start_hour}
            onValueChange={(v) => setConfig({ ...config, work_start_hour: v })}
          >
            <SelectTrigger data-testid="work-start-hour">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map(h => (
                <SelectItem key={h} value={h.toString()}>
                  {h.toString().padStart(2, '0')}:00
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>{t('dayEndHour')}</Label>
          <Select
            value={config.work_end_hour}
            onValueChange={(v) => setConfig({ ...config, work_end_hour: v })}
          >
            <SelectTrigger data-testid="work-end-hour">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {HOURS.map(h => (
                <SelectItem key={h} value={h.toString()}>
                  {h.toString().padStart(2, '0')}:00
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Default Day Duration & Break Settings */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5" />
          {t('defaultDuration')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
          <div className="space-y-2">
            <Label>{t('defaultDayDuration')}</Label>
            <Input
              type="number"
              step="0.5"
              min="1"
              max="12"
              value={config.default_day_duration || 8.5}
              onChange={(e) => setConfig({ ...config, default_day_duration: parseFloat(e.target.value) || 8.5 })}
              data-testid="default-day-duration"
            />
            <p className="text-xs text-muted-foreground">{t('hours')}</p>
          </div>
          <div className="space-y-2">
            <Label>{t('defaultBreakDuration')}</Label>
            <Input
              type="number"
              step="5"
              min="0"
              max="120"
              value={config.default_break_duration ?? 30}
              onChange={(e) => setConfig({ ...config, default_break_duration: e.target.value === '' ? 0 : parseInt(e.target.value) })}
              data-testid="default-break-duration"
            />
            <p className="text-xs text-muted-foreground">{t('minutes')}</p>
          </div>
          <div className="space-y-2">
            <Label>{t('breakThreshold')}</Label>
            <Input
              type="number"
              step="0.5"
              min="1"
              max="12"
              value={config.break_threshold_hours || 6}
              onChange={(e) => setConfig({ ...config, break_threshold_hours: parseFloat(e.target.value) || 6 })}
              data-testid="break-threshold-hours"
            />
            <p className="text-xs text-muted-foreground">{t('breakThresholdDesc')}</p>
          </div>
        </div>
      </div>

      <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <p className="text-sm text-blue-700 dark:text-blue-400">
          <strong>{t('note')}:</strong> {t('workHoursNote')}
        </p>
      </div>

      {/* Holiday Country Selection */}
      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-medium flex items-center gap-2 mb-4">
          <Globe className="h-5 w-5" />
          {t('holidayCountry')}
        </h3>
        <div className="max-w-xs space-y-2">
          <Label>{t('selectCountry')}</Label>
          <Select
            value={config.holiday_country || 'CH'}
            onValueChange={(v) => setConfig({ ...config, holiday_country: v })}
          >
            <SelectTrigger data-testid="holiday-country-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CH">
                <span className="flex items-center gap-2">🇨🇭 {t('switzerland')}</span>
              </SelectItem>
              <SelectItem value="FR">
                <span className="flex items-center gap-2">🇫🇷 {t('france')}</span>
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            <strong>{t('note')}:</strong> {t('holidayCountryNote')}
          </p>
        </div>
      </div>

      {/* Holidays List */}
      <HolidaysList language={language} />

      {/* Team Calendar iCal */}
      <TeamCalendarLink />

      <div className="flex gap-3 pt-4 border-t border-border">
        <Button
          onClick={() => onSave('schedule')}
          disabled={saving}
          data-testid="save-schedule-config-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          {saving ? t('saving') : t('save')}
        </Button>
      </div>
    </div>
  );
}
